import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { BlocksElement } from '../BlocksElement';
import type { StatusIndicatorType, StatusIndicatorVariant } from './status-indicator.model';
import styles from './status-indicator.styles';

/**
 * @csspart base - The component's base wrapper.
 */
export class BlocksStatusIndicator extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * The status indicator text
   */
  @property({ type: String })
  text: string | undefined;

  /**
   * Variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li> **default**, The variant name will be renamed to **neutral** in major release 3.0.0 - makes the status indicator blue</li>
   * <li> **success**, The variant name will be  renamed to **active** in major release 3.0.0 - makes the status indicator green</li>
   * <li> **warning** - makes the status indicator orange</li>
   * <li> **error** - makes the status indicator red</li>
   * <li> **in-determined** - makes the status indicator gray</li>
   * <li> **caution**, This variant is same as warning. This is **deprecated** and will be removed in major release 3.0.0</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: StatusIndicatorVariant;

  /**
   * Disabled attribute
   */
  @property({ type: Boolean })
  disabled: boolean | undefined;

  /**
   * Type variant
   * <cat-text-passage size="sm">
   * <p>By default renders the text inside a pill shape with a background color.</p>
   * <ul>
   * <li> **bare** renders the text with a dot indicator and without a background color</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  type: StatusIndicatorType;

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-status-indicator', {
      'cat-c-status-indicator--success': this.variant === 'success',
      'cat-c-status-indicator--warning': this.variant === 'warning',
      'cat-c-status-indicator--error': this.variant === 'error',
      'cat-c-status-indicator--caution': this.variant === 'caution',
      'cat-c-status-indicator--indetermined': this.variant === 'in-determined',
      'cat-c-status-indicator--bare': this.type === 'bare',
      'cat-c-status-indicator--disabled': this.disabled === true
    });

    return html` <div part="base" class="${componentClassNames}">${this.text ? html` <span part="text">${this.text}</span> ` : nothing}</div> `;
  }
}

if (customElements.get('cat-status-indicator') === undefined) {
  customElements.define('cat-status-indicator', BlocksStatusIndicator);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-status-indicator': BlocksStatusIndicator;
  }
}
